import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2, Upload, FileCheck2, X, Loader2, IdCard, MapPin, Baby, CreditCard, HeartPulse, ShieldCheck, RefreshCw } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";

const LOGO = "/logo-aae.png";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FileUploadField({ label, hint, value, onChange, token, required }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 10 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API_BASE}/public-links/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ id: data.id, filename: data.filename });
      toast.success(`${data.filename} envoyé`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      {value?.id ? (
        <div className="flex items-center gap-2 mt-1 p-2 rounded-lg border border-secondary bg-secondary/20">
          <FileCheck2 className="h-4 w-4 text-primary shrink-0" />
          <div className="text-xs flex-1 truncate">{value.filename}</div>
          <button type="button" className="text-destructive hover:opacity-70" onClick={() => onChange(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className="mt-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-border/70 bg-muted cursor-pointer hover:bg-muted transition-colors text-sm">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span className="text-muted-foreground">{uploading ? "Envoi..." : "Choisir un fichier (PDF/image, max 10 Mo)"}</span>
          <input type="file" className="hidden" accept="application/pdf,image/*" onChange={handleFile} disabled={uploading} />
        </label>
      )}
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

const EMPTY = {
  civilite: "Madame", first_name: "", last_name: "", nom_naissance: "",
  adresse: "", code_postal: "", ville: "",
  libelle_pays: "FRANCE", libelle_pays_nationalite: "FRANCE",
  phone: "", email: "",
  date_naissance: "", lieu_naissance: "", departement_naissance: "",
  libelle_pays_naissance: "FRANCE",
  numero_ss: "", numero_conges_spectacles: "",
  iban: "", bic: "",
  date_visite_medicale: "", visite_medicale_en_attente: false,
  doc_carte_vitale: null, doc_rib: null, doc_attestation_medicale: null,
  start_date: "", end_date: "", start_time: "",
  cost_ht_client: 0, event_name: "", event_location: "", notes: "",
};

const Section = ({ icon: Icon, title, children }) => (
  <Card className="border-border/60 shadow-warm"><CardContent className="p-6 space-y-4">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {title}
    </div>
    {children}
  </CardContent></Card>
);

export default function PublicDeclarationForm() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", config: null });
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", captcha_token: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = () => {
    axios.get(`${API_BASE}/public-links/${token}/captcha`)
      .then(({ data }) => { setCaptcha(data); setCaptchaAnswer(""); })
      .catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API_BASE}/public-links/${token}/form`)
      .then(({ data }) => {
        setState({ loading: false, error: "", config: data });
        if (data.intermittent) {
          setForm(f => ({ ...f, first_name: data.intermittent.first_name || "", last_name: data.intermittent.last_name || "", email: data.intermittent.email || "" }));
        }
        loadCaptcha();
      })
      .catch(e => setState({ loading: false, error: e?.response?.data?.detail || "Lien invalide ou expiré", config: null }));
  }, [token]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const onEventNameChange = (name) => {
    setForm(prev => {
      const match = state.config?.events?.find(e => e.name?.toLowerCase() === name.toLowerCase());
      return { ...prev, event_name: name, event_location: match?.location ? match.location : prev.event_location };
    });
  };

  const preFilled = !!state.config?.intermittent;
  const isFrance = (v) => (v || "").trim().toLowerCase() === "france";
  const isFrancais = (v) => (v || "").trim().toLowerCase().startsWith("français") || (v || "").trim().toLowerCase() === "française";

  const validate = () => {
    if (!preFilled) {
      const required = ["first_name", "last_name", "adresse", "code_postal", "ville",
                        "phone", "email", "date_naissance", "lieu_naissance",
                        "departement_naissance", "numero_ss", "iban", "bic"];
      for (const f of required) if (!form[f] || String(form[f]).trim() === "") return `Champ requis manquant`;
      if (!isFrance(form.libelle_pays) && !form.libelle_pays) return "Pays requis";
      if (!isFrancais(form.libelle_pays_nationalite) && !form.libelle_pays_nationalite) return "Nationalité requise";
      if (!isFrance(form.libelle_pays_naissance) && !form.libelle_pays_naissance) return "Pays de naissance requis";
      if (!form.doc_carte_vitale?.id) return "Photo recto carte vitale requise";
      if (!form.doc_rib?.id) return "RIB requis";
      if (!form.visite_medicale_en_attente) {
        if (!form.date_visite_medicale) return "Date de visite médicale ou 'en attente' requis";
        if (!form.doc_attestation_medicale?.id) return "Attestation visite médicale requise";
      }
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!captchaAnswer.trim()) { toast.error("Répondez à la question anti-robot"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        civilite: form.civilite,
        doc_carte_vitale_id: form.doc_carte_vitale?.id || "",
        doc_rib_id: form.doc_rib?.id || "",
        doc_attestation_medicale_id: form.doc_attestation_medicale?.id || "",
        cost_ht_client: parseFloat(form.cost_ht_client) || 0,
        captcha_answer: captchaAnswer.trim(),
        captcha_token: captcha.captcha_token,
      };
      // Remove local objects
      delete payload.doc_carte_vitale; delete payload.doc_rib; delete payload.doc_attestation_medicale;
      await axios.post(`${API_BASE}/public-links/${token}/submit`, payload);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
      loadCaptcha();  // regenerate captcha after failure
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement...</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-border/60 shadow-warm-lg"><CardContent className="p-8 text-center">
          <div className="text-destructive text-4xl mb-3">✕</div>
          <h2 className="font-brand text-2xl mb-2">Lien inaccessible</h2>
          <p className="text-muted-foreground text-sm">{state.error}</p>
          <p className="text-xs text-muted-foreground mt-4">Contactez l'équipe AAE pour obtenir un nouveau lien.</p>
        </CardContent></Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-border/60 shadow-warm-lg"><CardContent className="p-8 text-center">
          <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4" />
          <h2 className="font-brand text-3xl mb-2">Merci !</h2>
          <p className="text-muted-foreground">Votre inscription a été transmise à l'équipe Antoine Événements.</p>
          <p className="text-xs text-muted-foreground mt-6">Nous reviendrons vers vous rapidement pour valider votre fiche.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="border-b border-border/60 bg-card">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO} alt="Antoine Événements" className="h-10 w-auto" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Formulaire d'inscription nouvel intermittent du spectacle</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Intermittent du spectacle · inscription</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Renseignez votre fiche</h1>
          {state.config?.label && <p className="text-sm text-muted-foreground mt-2">{state.config.label}</p>}
          {!preFilled && <p className="text-xs text-muted-foreground mt-2">Tous les champs marqués d'un * sont obligatoires.</p>}
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="public-form">
          {!preFilled && (
            <>
              <Section icon={IdCard} title="Identité">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Civilité *</Label>
                    <Select value={form.civilite} onValueChange={v => set({ civilite: v })}>
                      <SelectTrigger data-testid="pub-civilite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Madame">Madame</SelectItem>
                        <SelectItem value="Monsieur">Monsieur</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div /><div>
                    <Label>Nom *</Label>
                    <Input required value={form.last_name} onChange={e => set({ last_name: e.target.value })} data-testid="pub-last" />
                  </div>
                  <div>
                    <Label>Prénom *</Label>
                    <Input required value={form.first_name} onChange={e => set({ first_name: e.target.value })} data-testid="pub-first" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Nom de naissance</Label>
                    <Input value={form.nom_naissance} onChange={e => set({ nom_naissance: e.target.value })} placeholder="Si différent" data-testid="pub-nom-naissance" />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input required value={form.phone} onChange={e => set({ phone: e.target.value })} data-testid="pub-phone" />
                  </div>
                  <div>
                    <Label>Mail *</Label>
                    <Input required type="email" value={form.email} onChange={e => set({ email: e.target.value })} data-testid="pub-email" />
                  </div>
                </div>
              </Section>

              <Section icon={MapPin} title="Adresse">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Adresse *</Label>
                    <Input required value={form.adresse} onChange={e => set({ adresse: e.target.value })} data-testid="pub-adresse" />
                  </div>
                  <div>
                    <Label>Code postal *</Label>
                    <Input required value={form.code_postal} onChange={e => set({ code_postal: e.target.value })} data-testid="pub-cp" />
                  </div>
                  <div>
                    <Label>Ville *</Label>
                    <Input required value={form.ville} onChange={e => set({ ville: e.target.value })} data-testid="pub-ville" />
                  </div>
                  <div>
                    <Label>Pays{!isFrance(form.libelle_pays) && " *"}</Label>
                    <Input list="countries-list" value={form.libelle_pays} onChange={e => set({ libelle_pays: e.target.value.toUpperCase() })} placeholder="FRANCE" data-testid="pub-pays" />
                    <div className="text-[11px] text-muted-foreground mt-1">Obligatoire si différent de France</div>
                  </div>
                  <div>
                    <Label>Nationalité{!isFrancais(form.libelle_pays_nationalite) && " *"}</Label>
                    <Input list="countries-list" value={form.libelle_pays_nationalite} onChange={e => set({ libelle_pays_nationalite: e.target.value.toUpperCase() })} placeholder="FRANCE" data-testid="pub-nationalite" />
                    <div className="text-[11px] text-muted-foreground mt-1">Obligatoire si différente</div>
                  </div>
                </div>
              </Section>

              <Section icon={Baby} title="Naissance">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Né(e) le *</Label>
                    <Input required value={form.date_naissance} onChange={e => set({ date_naissance: e.target.value })} placeholder="JJ/MM/AAAA" data-testid="pub-date-naissance" />
                  </div>
                  <div>
                    <Label>À (ville) *</Label>
                    <Input required value={form.lieu_naissance} onChange={e => set({ lieu_naissance: e.target.value })} data-testid="pub-lieu-naissance" />
                  </div>
                  <div>
                    <Label>Département *</Label>
                    <Input required value={form.departement_naissance} onChange={e => set({ departement_naissance: e.target.value })} placeholder="99 si hors France" data-testid="pub-dept-naissance" />
                    <div className="text-[11px] text-muted-foreground mt-1">Mettre 99 si né(e) hors France</div>
                  </div>
                  <div>
                    <Label>Pays de naissance{!isFrance(form.libelle_pays_naissance) && " *"}</Label>
                    <Input list="countries-list" value={form.libelle_pays_naissance} onChange={e => set({ libelle_pays_naissance: e.target.value.toUpperCase() })} placeholder="FRANCE" data-testid="pub-pays-naissance" />
                  </div>
                </div>
              </Section>

              <Section icon={CreditCard} title="Sécurité sociale & IBAN">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>NIR (n° de sécurité sociale) *</Label>
                    <Input required value={form.numero_ss} onChange={e => set({ numero_ss: e.target.value })} placeholder="1 84 07 75 116 246 13" data-testid="pub-nir" />
                  </div>
                  <FileUploadField
                    label="Recto de la carte vitale"
                    hint="PDF ou photo lisible"
                    value={form.doc_carte_vitale}
                    onChange={(v) => set({ doc_carte_vitale: v })}
                    token={token}
                    required
                  />
                  <div>
                    <Label>N° Congés Spectacles</Label>
                    <Input value={form.numero_conges_spectacles} onChange={e => set({ numero_conges_spectacles: e.target.value })} placeholder="Optionnel" data-testid="pub-conges" />
                  </div>
                  <div>
                    <Label>IBAN *</Label>
                    <Input required value={form.iban} onChange={e => set({ iban: e.target.value })} placeholder="FR76 ...." data-testid="pub-iban" />
                  </div>
                  <div>
                    <Label>BIC *</Label>
                    <Input required value={form.bic} onChange={e => set({ bic: e.target.value })} data-testid="pub-bic" />
                  </div>
                  <div className="md:col-span-2">
                    <FileUploadField
                      label="Relevé d'Identité Bancaire (RIB)"
                      hint="PDF de préférence"
                      value={form.doc_rib}
                      onChange={(v) => set({ doc_rib: v })}
                      token={token}
                      required
                    />
                  </div>
                </div>
              </Section>

              <Section icon={HeartPulse} title="Visite médicale">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 flex items-center gap-2">
                    <Checkbox id="med-attente" checked={form.visite_medicale_en_attente} onCheckedChange={v => set({ visite_medicale_en_attente: !!v })} data-testid="pub-med-attente" />
                    <Label htmlFor="med-attente" className="cursor-pointer">En attente d'un rendez-vous</Label>
                  </div>
                  {!form.visite_medicale_en_attente && (
                    <>
                      <div>
                        <Label>Date de la dernière visite médicale *</Label>
                        <Input value={form.date_visite_medicale} onChange={e => set({ date_visite_medicale: e.target.value })} placeholder="JJ/MM/AAAA" data-testid="pub-date-med" />
                      </div>
                      <div>
                        <FileUploadField
                          label="Attestation visite médicale"
                          hint="PDF ou photo"
                          value={form.doc_attestation_medicale}
                          onChange={(v) => set({ doc_attestation_medicale: v })}
                          token={token}
                          required
                        />
                      </div>
                    </>
                  )}
                </div>
              </Section>
            </>
          )}

          <Section icon={ShieldCheck} title="Vérification anti-robot">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <Label>{captcha.question || "Chargement..."} *</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={e => setCaptchaAnswer(e.target.value)}
                  placeholder="Votre réponse"
                  required
                  data-testid="pub-captcha"
                />
              </div>
              <div>
                <Button type="button" variant="outline" className="rounded-full" onClick={loadCaptcha} data-testid="pub-captcha-refresh">
                  <RefreshCw className="h-4 w-4 mr-2" /> Nouvelle question
                </Button>
                <div className="text-[11px] text-muted-foreground mt-2">Prouvez que vous n'êtes pas un robot.</div>
              </div>
            </div>
          </Section>

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} className="rounded-full h-12 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" data-testid="pub-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {submitting ? "Envoi..." : "Envoyer mon inscription"}
            </Button>
          </div>
        </form>

        <datalist id="countries-list">
          {COUNTRIES.map(c => (
            <option key={c.code} value={c.name} />
          ))}
        </datalist>

        <div className="text-center text-xs text-muted-foreground mt-8">
          Agence Antoine Événements · <a href="https://www.antoineevenements.fr" target="_blank" rel="noreferrer" className="text-primary underline">antoineevenements.fr</a>
        </div>
      </div>
    </div>
  );
}
